import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../DefaultComponent/BreadCrumb";
import Spinner from "../DefaultComponent/Spinner";
import { calculateTotalPages, formatDate } from "../../utils/Helper";
import { toast } from "react-toastify";
import {
  SkillsAdminListAction,
  SkillsBulkVerifyAction,
} from "../../Redux/Actions/SkillAction";
import CreateSkillsModal from "./CreateSkillsModal";

interface Skill {
  id: number;
  name: string;
  is_verified: boolean;
  mark_del: boolean;
  created_at: string;
  created_by?: {
    id: number;
    email: string;
    fullname: string;
  };
}

interface SkillFilters {
  name: string;
  is_verified: string;
  mark_del: string;
  created_by: string;
}

const defaultFilters: SkillFilters = {
  name: "",
  is_verified: "",
  mark_del: "",
  created_by: "",
};

const Skills: React.FC = () => {
  const dispatch = useDispatch<any>();
  const skillsState = useSelector((state: any) => state.SkillsAdminListData);
  const bulkVerifyState = useSelector(
    (state: any) => state.SkillsBulkVerifyData
  );

  const [skills, setSkills] = useState<Skill[]>([]);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage] = useState<number>(10);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [filters, setFilters] = useState<SkillFilters>(defaultFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<SkillFilters>(defaultFilters);
  const [selectedSkills, setSelectedSkills] = useState<Map<number, boolean>>(
    new Map()
  );

  const buildQueryParams = useCallback(
    (pageNumber: number, filterSet: SkillFilters) => {
      const params = new URLSearchParams();
      params.append("page", String(pageNumber));
      params.append("page_size", String(rowsPerPage));

      if (filterSet.name.trim()) params.append("name", filterSet.name.trim());
      if (filterSet.is_verified !== "")
        params.append("is_verified", filterSet.is_verified);
      if (filterSet.mark_del !== "")
        params.append("mark_del", filterSet.mark_del);
      if (filterSet.created_by.trim())
        params.append("created_by", filterSet.created_by.trim());

      return `?${params.toString()}`;
    },
    [rowsPerPage]
  );

  const fetchSkills = useCallback(
    (pageNumber: number, filterSet: SkillFilters) => {
      setTableLoading(true);
      const query = buildQueryParams(pageNumber, filterSet);
      dispatch(SkillsAdminListAction(query)).finally(() =>
        setTableLoading(false)
      );
    },
    [buildQueryParams, dispatch]
  );

  useEffect(() => {
    fetchSkills(page, appliedFilters);
  }, [page, appliedFilters, fetchSkills]);

  useEffect(() => {
    if (skillsState?.data?.row) {
      setSkills(skillsState.data.row);
    } else {
      setSkills([]);
    }
  }, [skillsState]);

  useEffect(() => {
    if (!skills.length) return;
    setSelectedSkills((prev) => {
      let changed = false;
      const next = new Map(prev);
      skills.forEach((skill) => {
        if (next.has(skill.id) && next.get(skill.id) !== skill.is_verified) {
          next.set(skill.id, skill.is_verified);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [skills]);

  const handleFilterChange = (field: keyof SkillFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(1);
  };

  const handleRefreshAfterCreate = () => {
    setPage(1);
    fetchSkills(1, appliedFilters);
  };

  const toggleSkillSelection = (skill: Skill) => {
    setSelectedSkills((prev) => {
      const next = new Map(prev);
      if (next.has(skill.id)) {
        next.delete(skill.id);
      } else {
        next.set(skill.id, skill.is_verified);
      }
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedSkills((prev) => {
      const next = new Map(prev);
      const allSelected = skills.every((skill) => next.has(skill.id));
      if (allSelected) {
        skills.forEach((skill) => next.delete(skill.id));
      } else {
        skills.forEach((skill) => next.set(skill.id, skill.is_verified));
      }
      return next;
    });
  };

  const selectedCount = selectedSkills.size;
  const selectedStatusSet = useMemo(
    () => new Set(Array.from(selectedSkills.values())),
    [selectedSkills]
  );
  const hasMixedStatuses = selectedStatusSet.size > 1;
  const canBulkVerify = selectedCount > 0 && !hasMixedStatuses;

  const handleBulkVerify = async (targetStatus: boolean) => {
    if (!selectedCount) {
      toast.error("Select at least one skill to update.");
      return;
    }
    if (hasMixedStatuses) {
      toast.error(
        "Select only verified or only unverified skills for bulk update."
      );
      return;
    }
    const skillIds = Array.from(selectedSkills.keys());
    const response = await dispatch(
      SkillsBulkVerifyAction(skillIds, targetStatus)
    );
    if (response?.status === true) {
      // Clear selection after successful update
      setSelectedSkills(new Map());
      fetchSkills(page, appliedFilters);
    }
  };

  const loading =
    tableLoading || skillsState?.loading || bulkVerifyState?.loading;

  return (
    <>
      {loading && <Spinner />}
      <div className="page-wrapper card" style={{ display: "flex" }}>
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Skills"
            breadcrumbs={[{ label: "Skills", path: "/skills", isActive: true }]}
          />
        </div>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            p: 2,
          }}
        >
          <Paper sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Typography variant="h6">Filter Skills</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={handleResetFilters}>
                  Clear
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApplyFilters}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} mt={2}>
              <TextField
                label="Skill name"
                value={filters.name}
                onChange={(e) => handleFilterChange("name", e.target.value)}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Verified</InputLabel>
                <Select
                  value={filters.is_verified}
                  label="Verified"
                  onChange={(e) =>
                    handleFilterChange("is_verified", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Verified</MenuItem>
                  <MenuItem value="false">Not Verified</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Marked Deleted</InputLabel>
                <Select
                  value={filters.mark_del}
                  label="Marked Deleted"
                  onChange={(e) =>
                    handleFilterChange("mark_del", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Created by (user id)"
                value={filters.created_by}
                onChange={(e) =>
                  handleFilterChange("created_by", e.target.value)
                }
                fullWidth
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="subtitle1">
                  Selected: {selectedCount}
                </Typography>
                {selectedCount > 0 && hasMixedStatuses && (
                  <Typography variant="body2" color="error">
                    Select only verified or only unverified to bulk update.
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="success"
                  disabled={!canBulkVerify}
                  onClick={() => handleBulkVerify(true)}
                >
                  Mark Verified
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={!canBulkVerify}
                  onClick={() => handleBulkVerify(false)}
                >
                  Mark Unverified
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenCreateModal(true)}
                >
                  Create Skills
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table aria-label="Skills table">
              <TableHead sx={{ backgroundColor: "#0555c8" }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={
                        selectedCount > 0 &&
                        selectedCount < skills.length &&
                        skills.some((skill) => selectedSkills.has(skill.id))
                      }
                      checked={
                        skills.length > 0 &&
                        skills.every((skill) => selectedSkills.has(skill.id))
                      }
                      onChange={toggleSelectAllOnPage}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>S.No</TableCell>
                  <TableCell sx={{ color: "white" }}>Skill</TableCell>
                  <TableCell sx={{ color: "white" }}>Verified</TableCell>
                  <TableCell sx={{ color: "white" }}>Marked Deleted</TableCell>
                  <TableCell sx={{ color: "white" }}>Created By</TableCell>
                  <TableCell sx={{ color: "white" }}>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skills.length > 0 ? (
                  skills.map((skill, idx) => (
                    <TableRow key={skill.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={selectedSkills.has(skill.id)}
                          onChange={() => toggleSkillSelection(skill)}
                        />
                      </TableCell>
                      <TableCell>
                        {(page - 1) * rowsPerPage + idx + 1}
                      </TableCell>
                      <TableCell>{skill.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={skill.is_verified ? "Verified" : "Pending"}
                          color={skill.is_verified ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={skill.mark_del ? "Yes" : "No"}
                          color={skill.mark_del ? "error" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {skill?.created_by?.fullname ||
                          skill?.created_by?.email ||
                          "-"}
                      </TableCell>
                      <TableCell>{formatDate(skill.created_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No skills found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="pagination-style">
            <Stack spacing={2}>
              <Pagination
                count={calculateTotalPages(
                  skillsState?.data?.total_count || 0,
                  rowsPerPage
                )}
                page={page}
                onChange={(_e: any, newPage) => setPage(newPage)}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </Stack>
          </div>
        </Box>
      </div>

      <CreateSkillsModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSuccess={handleRefreshAfterCreate}
      />
    </>
  );
};

export default Skills;
